/**
 * Admin V3 — Financial / Payouts — Table component
 *
 * 8 columns: (expand) | Date / Heure | Statut | Chauffeur | Course
 *           | Montant | Identifiant Stripe (truncated) | Actions.
 *
 * Per-row expandable detail:
 *   - Full payout_id
 *   - Full Stripe Transfer ID (when present, status = paid|paid_out)
 *   - Created at / released at / paid at (whichever are present)
 *   - Operator intervention metadata (triggered_at, triggered_by, reason)
 *   - Raw failure reason (when status = failed)
 *   - tenant_id
 *
 * Failed AND reversed rows: rose left-border.
 *
 * Course (booking) column links to /admin/bookings/:id (legacy V2 detail).
 * Chauffeur cell links to /admin-v3/drivers/:id.
 *
 * Driver Stripe ACCOUNT id (stripe_account_id) is NOT consumed — read-only-side
 * defense-in-depth. Only Stripe TRANSFER id (proof of completed transfer) is
 * surfaced.
 *
 * NO mutation surface.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import bookingDetailPath from '../bookings/bookingDetailPath';
import { Coins, AlertCircle, RotateCcw, ChevronRight, ChevronDown, ExternalLink } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  cn,
  currency,
  EmptyState,
  SmallPill,
  payoutStatusMeta,
} from '../../shared/visualTokens';
import { t } from '../../i18n';

const formatTs = (iso, locale) => {
  if (!iso) return '—';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'fr-FR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).format(d);
  } catch {
    return String(iso);
  }
};

const truncateMiddle = (s, prefixLen = 6, suffixLen = 4) => {
  if (!s) return '';
  if (s.length <= prefixLen + suffixLen + 1) return s;
  return `${s.slice(0, prefixLen)}…${s.slice(-suffixLen)}`;
};

const truncateEnd = (s, n = 12) => {
  if (!s) return '';
  if (s.length <= n) return s;
  return s.slice(0, n) + '…';
};

const SkeletonRows = () => (
  <>
    {Array(5).fill(0).map((_, i) => (
      <tr key={`sk-${i}`} className="border-b border-slate-100">
        {Array(8).fill(0).map((_, j) => (
          <td key={j} className="px-4 py-3.5">
            <div className="h-3 w-full max-w-[120px] rounded bg-slate-100 animate-pulse" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const DetailPanel = ({ payout, locale }) => {
  // paid_at appears as either `paid_out_at` (legacy) or `paid_at` (canonical).
  const paidAt = payout.paid_at || payout.paid_out_at || null;

  const rows = [
    (payout.payout_id || payout.id) && [t('financial.payouts.detail.payoutId',     locale), payout.payout_id || payout.id],
    payout.stripe_transfer_id   && [t('financial.payouts.detail.stripeId',      locale), payout.stripe_transfer_id],
    payout.created_at           && [t('financial.payouts.detail.createdAt',     locale), formatTs(payout.created_at, locale)],
    payout.released_at          && [t('financial.payouts.detail.releasedAt',    locale), formatTs(payout.released_at, locale)],
    paidAt                      && [t('financial.payouts.detail.paidAt',        locale), formatTs(paidAt, locale)],
    payout.triggered_at         && [t('financial.payouts.detail.triggeredAt',   locale), formatTs(payout.triggered_at, locale)],
    payout.triggered_by         && [t('financial.payouts.detail.triggeredBy',   locale), payout.triggered_by],
    payout.intervention_reason  && [t('financial.payouts.detail.interventionReason', locale), payout.intervention_reason],
    (payout.status === 'failed' && (payout.failure_reason || payout.error))
                                && [t('financial.payouts.detail.failureReason', locale), payout.failure_reason || payout.error],
    payout.tenant_id            && [t('financial.payouts.detail.tenant',        locale), payout.tenant_id],
  ].filter(Boolean);

  if (rows.length === 0) {
    return <div className="text-[11px] italic text-slate-400 px-2 py-1">—</div>;
  }

  return (
    <div className="bg-slate-50/60 border border-slate-200 rounded-xl p-4 my-2">
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
        {rows.map(([label, value]) => (
          <div key={label} className="text-[12px]">
            <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">{label}</dt>
            <dd className="text-slate-700 font-mono break-all">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

const FinancialPayoutsTable = ({ payouts, loading, error, isFilterActive, onRetry, locale = 'fr' }) => {
  const [expanded, setExpanded] = useState({});

  const toggleRow = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const errorBanner = error ? (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 mb-3" data-testid="adminv3-financial-payouts-error">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-rose-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-rose-700">{t('financial.payouts.error.title', locale)}</p>
          <p className="text-xs text-rose-600 mt-1 break-words">{error}</p>
        </div>
        <Button
          variant="outline" size="sm" onClick={onRetry}
          className="rounded-lg border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
          data-testid="adminv3-financial-payouts-error-action"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          {t('financial.payouts.error.retry', locale)}
        </Button>
      </div>
    </div>
  ) : null;

  const emptyContent = (
    <EmptyState
      icon={Coins}
      title={t('financial.payouts.empty.title', locale)}
      desc={t(isFilterActive ? 'financial.payouts.empty.descFilter' : 'financial.payouts.empty.descNoFilter', locale)}
    />
  );

  return (
    <>
      {errorBanner}
      <div className="overflow-x-auto rounded-2xl border border-slate-200">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-2 py-3 w-8" />
              {[
                t('financial.payouts.col.datetime', locale),
                t('financial.payouts.col.status',   locale),
                t('financial.payouts.col.driver',   locale),
                t('financial.payouts.col.booking',  locale),
                t('financial.payouts.col.amount',   locale),
                t('financial.payouts.col.stripeId', locale),
                t('financial.payouts.col.actions',  locale),
              ].map((h, i) => (
                <th key={i} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : payouts.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8">{emptyContent}</td>
              </tr>
            ) : (
              payouts.map((p, idx) => {
                const payoutId = p.payout_id || p.id || `${p.created_at}-${p.driver_id}-${idx}`;
                const key = payoutId;
                const isExpanded = Boolean(expanded[key]);
                const statusKey = p.status || 'pending';
                const statusMeta = payoutStatusMeta[statusKey] || {
                  label: statusKey,
                  pill: 'bg-slate-100 text-slate-500 border border-slate-200',
                };
                const isAlertRow = statusKey === 'failed' || statusKey === 'reversed';
                const driverPath = p.driver_id ? `/admin/drivers/${p.driver_id}` : null;
                const bookingPath = p.booking_id ? bookingDetailPath(p.booking_id) : null;

                return (
                  <React.Fragment key={key}>
                    <tr
                      className={cn(
                        'border-b border-slate-100 hover:bg-slate-50/70 transition-colors',
                        isAlertRow && 'bg-rose-50/30'
                      )}
                      style={isAlertRow ? { boxShadow: 'inset 3px 0 0 #ef4444' } : undefined}
                    >
                      <td className="px-2 py-3.5 align-top">
                        <button
                          type="button"
                          onClick={() => toggleRow(key)}
                          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                          title={isExpanded ? t('financial.payouts.detail.hide', locale) : t('financial.payouts.detail.show', locale)}
                          data-testid={`adminv3-financial-payouts-expand-${key}`}
                        >
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>

                      {/* Date / Heure */}
                      <td className="px-4 py-3.5 text-slate-500 text-[12px] whitespace-nowrap">
                        {formatTs(p.created_at, locale)}
                      </td>

                      {/* Statut */}
                      <td className="px-4 py-3.5">
                        <SmallPill meta={statusMeta} />
                      </td>

                      {/* Chauffeur */}
                      <td className="px-4 py-3.5 font-mono text-[12px]">
                        {driverPath ? (
                          <Link
                            to={driverPath}
                            className="inline-flex items-center gap-1 text-indigo-700 hover:text-indigo-900 hover:underline"
                            data-testid={`adminv3-financial-payouts-driver-${key}`}
                          >
                            {truncateEnd(p.driver_id, 12)}
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>

                      {/* Course */}
                      <td className="px-4 py-3.5 font-mono text-[12px]">
                        {bookingPath ? (
                          <Link
                            to={bookingPath}
                            className="inline-flex items-center gap-1 text-indigo-700 hover:text-indigo-900 hover:underline"
                            data-testid={`adminv3-financial-payouts-booking-${key}`}
                          >
                            {truncateEnd(p.booking_id, 12)}
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>

                      {/* Montant */}
                      <td className="px-4 py-3.5 font-bold text-slate-900 whitespace-nowrap tabular-nums">
                        {currency(p.amount ?? p.driver_payout ?? 0)}
                      </td>

                      {/* Identifiant Stripe (truncated; only when present) */}
                      <td className="px-4 py-3.5 font-mono text-[11px] text-slate-500">
                        {p.stripe_transfer_id ? truncateMiddle(p.stripe_transfer_id, 6, 4) : '—'}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3.5">
                        <button
                          type="button"
                          onClick={() => toggleRow(key)}
                          className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                          title={t('financial.payouts.action.open', locale)}
                          data-testid={`adminv3-financial-payouts-open-${key}`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="border-b border-slate-100">
                        <td colSpan={8} className="px-4 py-2">
                          <DetailPanel payout={p} locale={locale} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default FinancialPayoutsTable;
